/**
 * Spinner with an optional message, sized by pixel `size` (default 48) or
 * stretched to its container with `fitParent`. When `success` turns true it
 * swaps to a checkmark plus successMessage for 2 seconds, then fires
 * onSuccessComplete. The app-wide loading indicator: used by ItemCollection,
 * PaginatedList, PaymentMethodForm, and many bounty/submission/admin pages.
 */
import { Loader2, Check } from "lucide-react";
import { useEffect, useState } from "react";

type LoadingSpinnerProps = {
    className?: string;
    message?: string;
    size?: number; // pixel size when not fitting parent
    fitParent?: boolean; // if true, spinner grows to fill its container
    success?: boolean; // if true, shows success state with checkmark
    successMessage?: string; // message to show in success state
    onSuccessComplete?: () => void; // callback when success display is complete
};

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
    className,
    message,
    size = 48,
    fitParent = false,
    success = false,
    successMessage,
    onSuccessComplete,
}) => {
    const [showSuccess, setShowSuccess] = useState(false);

    useEffect(() => {
        if (success) {
            setShowSuccess(true);
            const timer = setTimeout(() => {
                setShowSuccess(false);
                onSuccessComplete?.();
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [success, onSuccessComplete]);

    const style = fitParent
        ? { width: "100%", height: "100%" }
        : { width: `${size}px`, height: `${size}px` };

    const displayMessage = showSuccess ? successMessage : message;

    return (
        <div
            className={`flex flex-col items-center justify-center ${
                fitParent ? "h-full w-full" : ""
            } ${className ?? ""}`}
        >
            {showSuccess ? (
                <Check style={style} className="text-black" />
            ) : (
                <Loader2 style={style} className="text-slate-950 animate-spin" />
            )}
            {displayMessage && (
                <span
                    className={`mt-[2px] text-sm ${showSuccess ? "text-black" : "text-slate-900"}`}
                >
                    {displayMessage}
                </span>
            )}
        </div>
    );
};

export default LoadingSpinner;
